/**
 * ============================================================================
 * Production 模块 - 枚举定义
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义生产模块相关的所有枚举类型
 * - 包含：ProductionOrderStatus, WorkOrderStatus, WorkReportStatus 等
 * 
 * 📖 对应后端：
 * - Model: backend/app/Modules/Production/Models/ProductionOrder.php
 * - Model: backend/app/Modules/Production/Models/WorkOrder.php
 * - Model: backend/app/Modules/Production/Models/WorkReport.php
 * - Model: backend/app/Modules/Production/Models/MaterialConsumption.php
 * - Model: backend/app/Modules/Production/Models/ScrapOrder.php
 * - Model: backend/app/Modules/Production/Models/ReworkOrder.php
 * - Model: backend/app/Modules/Production/Models/ProductionBatch.php
 * 
 * ⚠️ 注意事项：
 * - 枚举值必须与后端完全一致
 * - 每个枚举值必须添加中文注释
 * - 使用 TypeScript enum 而不是 type
 * 
 * 🔄 更新时机：
 * - 后端新增枚举值时
 * - 后端修改枚举值时
 * - 业务新增状态/类型时
 * 
 * ✅ 验证方式：
 * 让AI执行以下检查：
 * 1. 读取后端各Model文件
 * 2. 找到所有 const STATUS_*, const PRIORITY_*, const TYPE_* 等常量定义
 * 3. 检查枚举值、名称是否与后端一致
 * 4. 检查是否有遗漏或多余的枚举值
 * 
 * @module types/production/enums
 * @generated Manual (AI-assisted)
 * @last-updated 2025-11-26
 * ============================================================================
 */

/**
 * 生产订单状态枚举
 * 
 * 流程：待审核 → 已审核(待接单) → 已接单 → 进行中 → 已完成
 * 
 * 对应后端: ProductionOrder::STATUS_*
 */
export enum ProductionOrderStatus {
  /** 待审核 */
  PENDING = 1,
  
  /** 已审核（待接单） */
  APPROVED = 2,
  
  /** 已接单（可分配工单） */
  ACCEPTED = 3,
  
  /** 进行中 */
  IN_PROGRESS = 4,
  
  /** 已完成 */
  COMPLETED = 5,
  
  /** 已暂停 */
  PAUSED = 6,
  
  /** 已取消 */
  CANCELLED = 7,
}

/**
 * 生产订单优先级枚举
 * 
 * 对应后端: ProductionOrder::PRIORITY_*
 */
export enum ProductionOrderPriority {
  /** 紧急 */
  EMERGENCY = 1,
  
  /** 高 */
  HIGH = 2,
  
  /** 中 */
  MEDIUM = 3,
  
  /** 低 */
  LOW = 4,
}

/**
 * 生产订单来源类型枚举
 * 
 * 对应后端: ProductionOrder::SOURCE_*
 */
export enum ProductionOrderSourceType {
  /** 销售订单 */
  SALES_ORDER = 'SALES_ORDER',
  
  /** 手工创建 */
  MANUAL = 'MANUAL',
}

/**
 * 工单状态枚举
 * 
 * 对应后端: WorkOrder::STATUS_*
 */
export enum WorkOrderStatus {
  /** 待接单 */
  PENDING = 1,
  
  /** 已接单 */
  ACCEPTED = 2,
  
  /** 进行中 */
  IN_PROGRESS = 3,
  
  /** 已完成 */
  COMPLETED = 4,
  
  /** 已暂停 */
  PAUSED = 5,
  
  /** 已取消 */
  CANCELLED = 6,
}

/**
 * 工单优先级枚举
 * 
 * 对应后端: WorkOrder::PRIORITY_*
 */
export enum WorkOrderPriority {
  /** 紧急 */
  EMERGENCY = 1,
  
  /** 高 */
  HIGH = 2,
  
  /** 中 */
  MEDIUM = 3,
  
  /** 低 */
  LOW = 4,
}

/**
 * 工单工序状态枚举
 * 
 * 对应后端: WorkOrderOperation::STATUS_*
 */
export enum WorkOrderOperationStatus {
  /** 待开始 */
  PENDING = 1,

  /** 进行中 */
  IN_PROGRESS = 2,

  /** 已完成 */
  COMPLETED = 3,

  /** 已暂停 */
  PAUSED = 4,

  /** 已跳过 */
  SKIPPED = 5,
}

/**
 * 工单工序检验状态枚举
 * 
 * 对应后端: WorkOrderOperation::INSPECTION_*
 */
export enum WorkOrderOperationInspectionStatus {
  /** 待检验 */
  PENDING = 1,
  
  /** 检验通过 */
  PASSED = 2,
  
  /** 检验不通过 */
  REJECTED = 3,
}

/**
 * 物料消耗类型枚举
 * 
 * 对应后端: MaterialConsumption::TYPE_*
 */
export enum MaterialConsumptionType {
  /** 工单级消耗 */
  WORK_ORDER = 'WORK_ORDER',
  
  /** 工序级消耗 */
  OPERATION = 'OPERATION',
}

/**
 * 报废单状态枚举
 * 
 * 对应后端: ScrapOrder::STATUS_*
 */
export enum ScrapOrderStatus {
  /** 待审核 */
  PENDING = 1,
  
  /** 已审核 */
  APPROVED = 2,
  
  /** 已拒绝 */
  REJECTED = 3,
}

/**
 * 返工单状态枚举
 * 
 * 对应后端: ReworkOrder::STATUS_*
 */
export enum ReworkOrderStatus {
  /** 待审核 */
  PENDING = 1,
  
  /** 已审核 */
  APPROVED = 2,
  
  /** 处理中 */
  PROCESSING = 3,
  
  /** 已完成 */
  COMPLETED = 4,
  
  /** 已拒绝 */
  REJECTED = 5,
}

/**
 * 生产批次状态枚举
 * 
 * 对应后端: ProductionBatch::STATUS_*
 */
export enum ProductionBatchStatus {
  /** 进行中 */
  IN_PROGRESS = 'IN_PROGRESS',
  
  /** 已完成 */
  COMPLETED = 'COMPLETED',
}

/**
 * 工单类型枚举（多级BOM场景）
 * 
 * 对应后端: WorkOrder::TYPE_*
 */
export enum WorkOrderType {
  /** 成品工单 */
  FINAL = 'FINAL',
  
  /** 半成品工单 */
  SEMI = 'SEMI',
}

/**
 * 工单产出状态枚举（半成品工单）
 * 
 * 对应后端: WorkOrder::OUTPUT_STATUS_*
 */
export enum WorkOrderOutputStatus {
  /** 待产出 */
  PENDING = 'PENDING',
  
  /** 部分产出 */
  PARTIAL = 'PARTIAL',
  
  /** 已产出 */
  COMPLETED = 'COMPLETED',
}

